import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLoaderData } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';
import Rate from '@r6digital/rapidstor-types/types/Rate';

import { useGetCouponRatesMutation } from '@/api/rapidstor';
import Logger from '@/utils/logging';

import store from '@/store';
import {
    selectReservationData,
    selectUnitData,
    setReservationData,
    setUnitData,
} from '@/store/bookingSlice';

import Spinner from '@/components/elements/spinner';

import { ANIMATION_SPEED } from '@/features/rapidstor/animations';

import classes from './styles.module.scss';

export type Styling = 'base' | 'primary' | 'merch';

export interface CouponAlert {
    text: string;
    type: 'error' | 'success';
}

interface Props {
    type: Styling;
}

export default function Coupon({ type }: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const [code, setCode] = useState<string>('');
    const [activeCode, setActiveCode] = useState<string>('');
    const [alert, setAlert] = useState<CouponAlert | null>(null);
    const [fetchCouponRates] = useGetCouponRatesMutation();
    const [loading, setLoading] = useState<boolean>(false);

    const reservationData = useSelector(selectReservationData);
    const unitData = useSelector(selectUnitData);

    const getCouponRates = async (code: string, unitTypeId: string) => {
        const result = await fetchCouponRates({
            couponCode: code,
            unitTypeId: unitTypeId,
            corpCode: location.corpCode,
            locationCode: location.locationCode,
        }).unwrap();

        return result;
    };

    const postCouponApply = async () => {
        if (!reservationData || !unitData) {
            return null;
        }

        setLoading(true);

        try {
            const result = await getCouponRates(code, reservationData.unitType._id);

            if (result.deal) {
                const updatedUnitType = {
                    ...reservationData.unitType,
                    deal: result.deal,
                    rates: result.rates,
                };

                const descriptor = reservationData.descriptor;
                const newUnitTypes: UnitType[] = [];

                for (let unitType of descriptor.unitTypes) {
                    const unitTypeWithCoupon = await getCouponRates(code, unitType._id);
                    unitType = {
                        ...unitType,
                        deal: unitTypeWithCoupon.deal,
                        rates: unitTypeWithCoupon.rates,
                    };
                    newUnitTypes.push(unitType);
                }

                const newDescriptor = {
                    ...descriptor,
                    unitTypes: newUnitTypes,
                };

                const newDeal = {
                    ...result.deal,
                    rates: result.rates as Rate,
                };

                const newReservationData = {
                    ...reservationData,
                    deal: result.deal._id as string,
                    dealObject: newDeal,
                    unitType: updatedUnitType,
                    descriptor: newDescriptor,
                    coupon: code,
                };

                const newUnitData = {
                    ...unitData,
                    unitType: updatedUnitType,
                };

                store.dispatch(setUnitData(newUnitData));
                store.dispatch(setReservationData(newReservationData));
                setAlert({
                    text: t('coupon_code:alerts:success'),
                    type: 'success',
                });
                setLoading(false);
                setActiveCode(code);
            } else {
                setLoading(false);
            }
        } catch (error) {
            Logger.warn(
                `[postCouponApply] Failed to apply coupon code: ${JSON.stringify(error)}`,
                location,
                [
                    {
                        name: 'coupon_code',
                        value: code,
                    },
                ]
            );
            handleError();
        }
    };

    const handleError = () => {
        setActiveCode(code);
        setLoading(false);
        setAlert({
            text: t('coupon_code:alerts:error'),
            type: 'error',
        });
    };

    useEffect(() => {
        if (reservationData && reservationData.coupon === null) {
            setCode('');
            setActiveCode('');
            setAlert(null);
        }
    }, [reservationData]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
                duration: ANIMATION_SPEED,
                delay: ANIMATION_SPEED,
            }}
            className={`${classes.container} ${type === 'base' ? classes.base : classes.primary
                }`}
        >
            <div className={`${classes.header}`}>
                <div className={`${classes.left}`}>
                    <svg
                        width='19'
                        height='19'
                        viewBox='0 0 19 19'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <path d='M13.5194 3.49969C13.6648 3.28 13.7298 3.01684 13.7035 2.75471C13.6772 2.49259 13.5611 2.24761 13.3749 2.06123L12.1218 0.808134C11.9075 0.593807 11.6168 0.473399 11.3137 0.4734C11.0106 0.4734 10.7199 0.593808 10.5056 0.808135L0.808123 10.5056C0.593796 10.7199 0.473388 11.0106 0.473388 11.3137C0.473388 11.6168 0.593796 11.9075 0.808123 12.1218L2.06122 13.3749C2.2476 13.5611 2.49258 13.6772 2.7547 13.7035C3.01683 13.7298 3.27999 13.6648 3.49967 13.5194C3.82922 13.303 4.22327 13.2069 4.61542 13.2471C5.00758 13.2874 5.37389 13.4615 5.65264 13.7403C5.9314 14.019 6.10556 14.3853 6.14579 14.7775C6.18603 15.1697 6.08987 15.5637 5.87353 15.8932C5.72786 16.1133 5.66285 16.3771 5.68955 16.6396C5.71625 16.9022 5.83301 17.1475 6.02 17.3337L7.2731 18.5868C7.48743 18.8011 7.77812 18.9216 8.08122 18.9216C8.38432 18.9216 8.67502 18.8011 8.88934 18.5868L18.5868 8.88935C18.8011 8.67503 18.9215 8.38434 18.9215 8.08123C18.9215 7.77813 18.8011 7.48744 18.5868 7.27311L17.3337 6.02002C17.1473 5.83384 16.9023 5.71777 16.6402 5.69144C16.3781 5.66512 16.1149 5.73016 15.8953 5.87556C15.5657 6.09191 15.1717 6.18806 14.7795 6.14782C14.3873 6.10759 14.021 5.93343 13.7423 5.65467C13.4635 5.37592 13.2894 5.00961 13.2491 4.61745C13.2089 4.2253 13.3051 3.83125 13.5214 3.50171L13.5194 3.49969ZM6.82863 16.5261C7.19208 15.9767 7.35452 15.3186 7.28844 14.6631C7.22236 14.0077 6.93182 13.3952 6.46601 12.9294C6.0002 12.4636 5.38774 12.1731 4.73231 12.107C4.07688 12.0409 3.41875 12.2034 2.86934 12.5668L1.61625 11.3137L4.44467 8.48529L10.9096 14.9503L8.08122 17.7787L6.82863 16.5261ZM16.5261 6.82864L17.7787 8.08123L11.7178 14.1421L5.25279 7.67717L11.3137 1.61626L12.5663 2.86885C12.2028 3.41826 12.0404 4.07639 12.1065 4.73182C12.1726 5.38725 12.4631 5.99971 12.9289 6.46552C13.3947 6.93133 14.0072 7.22186 14.6626 7.28794C15.318 7.35403 15.9762 7.19159 16.5256 6.82814L16.5261 6.82864Z' />
                    </svg>
                    <span>{t('coupon_code:title')}</span>
                </div>
                <div className={`${classes.right}`}>
                    {alert && (
                        <span
                            className={`${alert.type === 'error' ? classes.error : classes.success
                                }`}
                        >
                            {alert.text}
                        </span>
                    )}
                </div>
            </div>
            <div className={`${classes.body}`}>
                <input
                    type='text'
                    placeholder={t('coupon_code:placeholder')}
                    onChange={(e) => setCode(e.target.value)}
                    value={code}
                    disabled={loading}
                    className={classes.input}
                />
                <button
                    disabled={loading || code === activeCode}
                    className={classes.button}
                    onClick={() => {
                        if (code !== '') {
                            postCouponApply();
                        }
                    }}
                >
                    {loading ? <Spinner size='20' /> : t('coupon_code:apply')}
                </button>
            </div>
        </motion.div>
    );
}
